"use client";

import { setAuthTokenCookieAction } from "@/actions/auth.actions";
import {
	UserMutationKeys,
	useUserService,
} from "@/services/client/hooks/use-user-service";
import { Button, type ButtonProps, Icon, useToast } from "@chakra-ui/react";
import { GoogleLogo } from "@phosphor-icons/react/dist/ssr";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FirebaseError } from "firebase/app";
import {
	GoogleAuthProvider,
	getAuth,
	getIdToken,
	getIdTokenResult,
	signInWithPopup,
	signOut,
} from "firebase/auth";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import { errorFirstWrapAsync } from "@flowpos-workspace/global/utils/promise.utils";

import { useFirebaseStore } from "@/stores/firebase.store";

interface OwnProperties {
	isAuthenticating: boolean;
	toggleIsAuthenticating: {
		on: () => void;
		off: () => void;
		toggle: () => void;
	};
}

export type AuthGoogleButtonProperties = OwnProperties & ButtonProps;

export const AuthGoogleButton = ({
	isAuthenticating,
	toggleIsAuthenticating,
	children,
	...rest
}: AuthGoogleButtonProperties) => {
	const { getFirebaseApp } = useFirebaseStore((state) => ({
		getFirebaseApp: state.getFirebaseApp,
	}));
	const [auth, setAuth] = useState<ReturnType<typeof getAuth> | null>(null);

	useEffect(() => {
		const initAuth = async () => {
			try {
				const firebaseApp = await getFirebaseApp();
				setAuth(getAuth(firebaseApp));
			} catch (error) {
				console.error("Failed to initialize Firebase auth:", error);
			}
		};

		initAuth();
	}, [getFirebaseApp]);

	const toast = useToast({ duration: 3000, status: "error", position: "top" });

	const { push, replace, refresh } = useRouter();

	const queryClient = useQueryClient();

	const { createUser } = useUserService();

	const createUserMutation = useMutation({
		mutationKey: [UserMutationKeys.CreateUser],
		mutationFn: createUser,
		onSettled: () => {
			queryClient.invalidateQueries();
		},
		onSuccess: async () => {
			if (auth?.currentUser) {
				const [tokenError, firebaseIdToken] = await errorFirstWrapAsync(
					getIdToken(auth.currentUser, true),
				);

				if (!tokenError && firebaseIdToken) {
					// Save the Firebase ID token to localStorage
					localStorage.setItem("firebaseAuthToken", firebaseIdToken);

					// Set the auth token cookie and redirect to dashboard
					try {
						// First set the cookie
						await setAuthTokenCookieAction({
							idToken: firebaseIdToken,
							shouldRedirect: false, // Don't redirect from the server action
						});

						// Then redirect on the client side
						refresh();
						push("/dashboard");
					} catch (cookieError) {
						console.log("Error setting auth cookie:", cookieError);
						// Still try to redirect even if cookie setting fails
						push("/dashboard");
					}
				} else {
					// If we can't get a token, just redirect
					push("/dashboard");
				}
			} else {
				// If there's no current user, just redirect
				push("/dashboard");
			}
		},
		onError: async () => {
			if (auth) {
				await signOut(auth);
			}
			replace("/sign-out");
			toast({
				description: "Unexpected error ocurred",
			});
		},
	});

	const showGoogleAuthPopup = async () => {
		if (!auth) {
			toast({
				description: "Authentication service not initialized yet.",
			});
			return;
		}

		const googleAuthProvider = new GoogleAuthProvider();

		// Set custom parameters to improve compatibility with third-party cookie restrictions
		googleAuthProvider.setCustomParameters({
			prompt: "select_account",
			// Use a more modern authentication flow
			auth_type: "reauthenticate",
		});

		toggleIsAuthenticating.on();

		try {
			// Use a more reliable approach for popup authentication
			const result = await signInWithPopup(auth, googleAuthProvider);
			const credentials = result;

			const [firebaseError, firebaseIdTokenResult] = await errorFirstWrapAsync(
				getIdTokenResult(credentials.user),
			);

			const [tokenError, firebaseIdToken] = await errorFirstWrapAsync(
				getIdToken(credentials.user),
			);

			if (tokenError ?? firebaseError) {
				await signOut(auth);
				replace("/sign-out");

				return toast({
					description: "There was an error getting authentication tokens.",
				});
			}

			// Save the Firebase ID token to localStorage
			if (firebaseIdToken) {
				localStorage.setItem("firebaseAuthToken", firebaseIdToken);
			}

			if (!firebaseIdTokenResult.claims.db_user_id) {
				return createUserMutation.mutate({
					firebaseIdToken,
				});
			}

			// Set the auth token cookie and redirect to dashboard
			try {
				// First set the cookie
				await setAuthTokenCookieAction({
					idToken: firebaseIdToken,
					shouldRedirect: false, // Don't redirect from the server action
				});

				// Then redirect on the client side
				refresh();
				push("/dashboard");
			} catch (cookieError) {
				console.log("Error setting auth cookie:", cookieError);
				// Still try to redirect even if cookie setting fails
				push("/dashboard");
			}
		} catch (error) {
			// Handle specific Firebase errors
			if (error instanceof FirebaseError) {
				switch (error.code) {
					case "auth/popup-closed-by-user":
						return toast({
							description: "Authentication popup was closed. Please try again.",
						});
					case "auth/popup-blocked":
						return toast({
							description:
								"Authentication popup was blocked. Please allow popups for this site.",
						});
					case "auth/cancelled-popup-request":
						return toast({
							description: "Authentication was cancelled. Please try again.",
						});
					default:
						// Log the error without using console.error to avoid Next.js error handling
						console.log("Firebase authentication error:", {
							code: error.code,
							message: error.message,
						});
						return toast({
							description: `Authentication error: ${error.message}`,
						});
				}
			}

			// For non-Firebase errors, log without using console.error
			console.log("Authentication error:", error);

			// Generic error message
			return toast({
				description:
					"There was an error authenticating you with Google. Please try again.",
			});
		} finally {
			toggleIsAuthenticating.off();
		}
	};

	if (!auth) {
		return null;
	}

	return (
		<Button
			onClick={showGoogleAuthPopup}
			isDisabled={isAuthenticating}
			leftIcon={<Icon as={GoogleLogo} fontSize="xl" />}
			{...rest}
		>
			{children}
		</Button>
	);
};
